import { useState } from "react"
import { DateTime } from "luxon"
import {
  CaretLeft,
  CaretRight,
  ClockCounterClockwise,
} from "@phosphor-icons/react"
import ApiError from "../../../components/ApiError"
import Loader from "../../../components/Loader"
import { formatISODate } from "../../../utils/datetime"
import { useGlobalSettingHistory } from "../../../hooks/useGlobalSettings"

const PAGE_SIZE = 10

const actionBadgeClasses = {
  insert: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  update: "bg-sky-50 text-sky-700 ring-sky-600/20",
}

const pagerButtonClasses =
  "flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 text-gray-600 transition hover:border-secondary hover:text-secondary disabled:cursor-not-allowed disabled:opacity-40"

const relativeTime = (value) =>
  value ? DateTime.fromISO(value).toRelative() : null

// Initials keep the "who" column scannable when several people edit settings.
const initials = (name) =>
  name
    .split(/[\s.\-_]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("")

const SettingHistory = ({ settingKey }) => {
  const [offset, setOffset] = useState(0)

  const { data, isLoading, isFetching, isError, error } =
    useGlobalSettingHistory({
      key: settingKey,
      offset,
      limit: PAGE_SIZE,
    })

  if (isError) return <ApiError axiosError={error} />
  if (isLoading) return <Loader className="my-6" />

  const rows = data?.data || []
  const total = data?.total || 0

  if (!rows.length) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white py-8 text-center">
        <ClockCounterClockwise className="text-slate-300" size={24} />
        <p className="text-sm text-gray-500">No changes recorded yet</p>
        <p className="text-xs text-gray-400">
          Every save is logged here with who made it
        </p>
      </div>
    )
  }

  return (
    <div
      className={`overflow-hidden rounded-lg border border-slate-200 bg-white transition-opacity ${
        isFetching ? "opacity-60" : ""
      }`}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-4 py-2.5 font-medium">When</th>
              <th className="px-4 py-2.5 font-medium">Action</th>
              <th className="px-4 py-2.5 font-medium">Value</th>
              <th className="px-4 py-2.5 font-medium">Performed by</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.historyId} className="transition hover:bg-slate-50">
                <td className="px-4 py-3 align-middle">
                  <div className="text-gray-900">
                    {formatISODate(row.performedAt)}
                  </div>
                  <div className="text-xs text-gray-400">
                    {relativeTime(row.performedAt)}
                  </div>
                </td>
                <td className="px-4 py-3 align-middle">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                      actionBadgeClasses[row.action] ||
                      "bg-gray-50 text-gray-700 ring-gray-600/20"
                    }`}
                  >
                    {row.action}
                  </span>
                </td>
                <td className="px-4 py-3 align-middle font-medium text-gray-900">
                  {row.value}
                </td>
                <td className="px-4 py-3 align-middle">
                  {/* performedBy is null for the seeded row, which no user made. */}
                  <span className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-gray-600">
                      {row.performedBy ? initials(row.performedBy) : "SYS"}
                    </span>
                    <span className="text-gray-700">
                      {row.performedBy || "System"}
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <footer className="flex items-center justify-between border-t border-slate-200 px-4 py-2.5 text-xs text-gray-500">
          <span>
            {`${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              aria-label="Previous page"
              className={pagerButtonClasses}
              disabled={offset === 0 || isFetching}
              onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
            >
              <CaretLeft size={12} weight="bold" />
            </button>
            <button
              aria-label="Next page"
              className={pagerButtonClasses}
              disabled={offset + PAGE_SIZE >= total || isFetching}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </footer>
      )}
    </div>
  )
}

export default SettingHistory
